import React from 'react';

import { Box, Icon, Text } from '@rbilabs/universal-components';
import { isNil } from 'lodash-es';
import { useIntl } from 'react-intl';
import { ScrollView, TouchableOpacity } from 'react-native';

import { Helmet } from 'components/helmet';
import LanguageSelector from 'components/language-selector';
import Link from 'components/link';
import { useRumPageView } from 'hooks/rum/use-rum-page-view';
import { useFeatureNavigation } from 'hooks/use-feature-navigation';
import { useIsLocalizationEnabled } from 'hooks/use-is-localization-enabled';
import { useDismissAllAndNavigate } from 'navigation/hooks/use-dismiss-and-navigate';
import { AccountLink } from 'pages/account/account-link';
import { AccountIcon } from 'pages/account/icons/account-icon';
import { CommunicationPreferencesIcon } from 'pages/account/icons/communication-preferences-icon';
import { PartnershipsIcon } from 'pages/account/icons/partnerships-icon';
import { PaymentMethodsIcon } from 'pages/account/icons/payment-methods-icon';
import { RecentOrdersIcon } from 'pages/account/icons/recent-orders-icon';
import { SubmitFeedbackIcon } from 'pages/account/icons/submit-feedback-icon';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyUserState } from 'state/loyalty/hooks/use-loyalty-user-state';
import { theme } from 'styles/configure-theme';
import { primitive } from 'styles/constants/primitives';
import { routes } from 'utils/routing';

import { AccountLoyaltyHeader } from './account-loyalty-header';
import Disclaimer from './disclaimer';
import ListOfLinks from './list-of-links';

const AccountAction = Link.withConfig({
  flexDirection: 'row',
  textDecoration: 'none',
  marginY: '2',
  alignItems: 'center',
});

const AccountLanguageSelectorContainer = Box.withConfig({
  borderBottomWidth: '1px',
  borderBottomColor: theme.token('border-color-default'),
  paddingBottom: '$1',
});

const LanguageSelectIntro = Text.withConfig({
  textTransform: 'none',
});

const StyledIcon = Box.withConfig({});

const ActionsWrapper = Box.withConfig({
  paddingBottom: '$1',
});

const AccountLinksWrapper = Box.withConfig({
  marginBottom: '$8',
  backgroundColor: primitive.$white,
  paddingX: '$4',
  paddingY: '$2',
  borderRadius: Styles.borderRadius,
});

export const AccountDrawerPage = ({ handleClose }: { handleClose: () => void }) => {
  useLoyaltyUserState({ refreshOnMountOrFocus: true });

  const { formatMessage } = useIntl();
  const enableOrderingFlag = useFlag(LaunchDarklyFlag.ENABLE_ORDERING);
  const enableOrdering = isNil(enableOrderingFlag) || enableOrderingFlag;
  const enableFindRestaurantLink = useFlag(LaunchDarklyFlag.ENABLE_FIND_RESTAURANT_LINK);
  const enableWalmartLink = useFlag(LaunchDarklyFlag.ENABLE_WALMART_PLUS_INTEGRATION);
  const accountLinksWithNewBadge = useFlag(LaunchDarklyFlag.ENABLE_ACCOUNT_NEW_BADGE);
  const isLocalizationEnabled = useIsLocalizationEnabled();
  const accountLinksWithNewBadgeEnabled = accountLinksWithNewBadge?.split(',') || [];
  const dismissAndNavigate = useDismissAllAndNavigate();

  const { isAuthenticated } = useAuthContext();

  useRumPageView('account', 'Account');

  const { aboutMenuEntries }: any = useFeatureNavigation();

  return (
    <>
      <Helmet title={formatMessage({ id: 'myAccount' })} />
      <TouchableOpacity onPress={handleClose} testID="modal-close-button" hitSlop={15}>
        <Icon variant="cancel" />
      </TouchableOpacity>
      <ScrollView showsVerticalScrollIndicator={false}>
        {isLocalizationEnabled && (
          <AccountLanguageSelectorContainer>
            <LanguageSelectIntro mb="$2">
              {formatMessage({ id: 'languageAndRegion' })}
            </LanguageSelectIntro>
            <LanguageSelector color={theme.token('text-default')} />
          </AccountLanguageSelectorContainer>
        )}
        <AccountLoyaltyHeader />
        {isAuthenticated && (
          <>
            <AccountLinksWrapper>
              <AccountLink
                link={routes.accountInfo}
                label={formatMessage({ id: 'accountInfo' })}
                leftIcon={<AccountIcon />}
                testID="account-info"
                showNewBadge={accountLinksWithNewBadgeEnabled.includes(routes.accountInfo)}
              />
              {enableOrdering && (
                <AccountLink
                  link={routes.payment}
                  label={formatMessage({ id: 'paymentMethods' })}
                  leftIcon={<PaymentMethodsIcon />}
                  testID="payment-methods"
                  showNewBadge={accountLinksWithNewBadgeEnabled.includes(routes.payment)}
                />
              )}
              {enableOrdering && (
                <AccountLink
                  link={routes.orders}
                  label={formatMessage({ id: 'recentOrders' })}
                  leftIcon={<RecentOrdersIcon />}
                  testID="recent-orders"
                  showNewBadge={accountLinksWithNewBadgeEnabled.includes(routes.orders)}
                />
              )}
              <AccountLink
                link={routes.communication}
                label={formatMessage({ id: 'communicationPreferences' })}
                leftIcon={<CommunicationPreferencesIcon />}
                testID="communicationPreferences"
                showNewBadge={accountLinksWithNewBadgeEnabled.includes(routes.communication)}
              />
              {enableWalmartLink && (
                <AccountLink
                  link={routes.partnerships}
                  label={formatMessage({ id: 'partnerships' })}
                  leftIcon={<PartnershipsIcon />}
                  testID="partnerships"
                  showNewBadge={accountLinksWithNewBadgeEnabled.includes(routes.partnerships)}
                />
              )}
              <AccountLink
                link={routes.support}
                label={formatMessage({ id: 'submitFeedback' })}
                leftIcon={<SubmitFeedbackIcon />}
                linkProps={{ borderBottomWidth: 0 }}
                showNewBadge={accountLinksWithNewBadgeEnabled.includes(routes.support)}
              />
            </AccountLinksWrapper>
          </>
        )}

        <ActionsWrapper>
          {enableFindRestaurantLink && (
            <AccountAction
              onPress={() => dismissAndNavigate(routes.storeLocator)}
              justifyContent={'center'}
            >
              <StyledIcon>
                <Icon variant="location" color={primitive.bk.$fieryRed} width="$6" aria-hidden />
              </StyledIcon>
              <Text bold color={primitive.bk.$fieryRed}>
                {formatMessage({ id: 'findARestaurant' })}
              </Text>
            </AccountAction>
          )}
        </ActionsWrapper>

        {aboutMenuEntries && (
          <ListOfLinks
            testID="links-list"
            title={aboutMenuEntries.name?.locale || ''}
            entries={aboutMenuEntries.pages || []}
            borderColor="black"
            sectionHeadings
          />
        )}
        <Disclaimer />
        <Link to={routes.diagnostics}>
          <Text>{formatMessage({ id: 'diagnostics' })}</Text>
        </Link>
      </ScrollView>
    </>
  );
};
